import {
  createClinicMutation,
  deleteClinicMutation,
  updateClinicMutation,
} from '@/api/clinics/clinicMutations';
import type { Clinic } from '@/api/clinics/clinicQueries';
import { getClinicsQuery } from '@/api/clinics/clinicQueries';
import { queryKeys } from '@/api/queryKeys';
import { Button } from '@/components/common/Button';
import { ConfirmDeleteDialog } from '@/components/common/ConfirmDeleteDialog';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { TableCell, TableHead } from '@/components/common/Table';
import type { ClinicNameFormValues } from '@/validation/clinicValidator';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';
import { ClinicsDialog } from './ClinicsDialog';

export default function Clinics() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);

  const { data: response, isLoading } = useQuery(getClinicsQuery(page));

  const clinics = response?.data ?? [];
  const meta = response?.meta;

  const [editingClinic, setEditingClinic] = useState<Clinic | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clinicIdToDelete, setClinicIdToDelete] = useState<number | null>(null);

  useEffect(() => {
    if (meta && page > meta.last_page) setPage(meta.last_page);
  }, [meta, page]);

  const { mutate: createClinic, isPending: isCreating } = useMutation({
    ...createClinicMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clinics.all() });
      setIsDialogOpen(false);
      toast.success('Clinic created successfully');
    },
  });

  const { mutate: updateClinic, isPending: isUpdating } = useMutation({
    ...updateClinicMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clinics.all() });
      setIsDialogOpen(false);
      setEditingClinic(null);
      toast.success('Clinic updated successfully');
    },
  });

  const { mutate: deleteClinic, isPending: isDeleting } = useMutation({
    ...deleteClinicMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clinics.all() });
      toast.success('Clinic deleted successfully');
    },
  });

  const handleAddNew = () => {
    setEditingClinic(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (clinic: Clinic) => {
    setEditingClinic(clinic);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setClinicIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (clinicIdToDelete !== null) {
      deleteClinic(clinicIdToDelete);
      setDeleteDialogOpen(false);
      setClinicIdToDelete(null);
    }
  };

  const handleSubmit = (values: ClinicNameFormValues) => {
    if (editingClinic) {
      updateClinic({ id: editingClinic.id, name: values.name.trim() });
    } else {
      createClinic({ name: values.name.trim() });
    }
  };

  return (
    <>
      <DashboardTableAccordion
        value="Clinics"
        title="Clinics"
        data={clinics}
        isLoading={isLoading}
        paginationMeta={meta}
        onPageChange={setPage}
        emptyMessage="No clinics found"
        tableWrapper="card"
        renderHeader={() => (
          <>
            <TableHead>Name</TableHead>
            <TableHead className="w-48" />
          </>
        )}
        renderRow={(clinic) => (
          <>
            <TableCell>{clinic.name}</TableCell>
            <TableCell>
              <div className="flex justify-end gap-3">
                <RelationActionButton
                  type="button"
                  onClick={() => handleEdit(clinic)}
                >
                  Edit
                </RelationActionButton>
                <RelationActionButton
                  type="button"
                  onClick={() => handleDeleteClick(clinic.id)}
                  className="text-delete"
                >
                  Remove
                </RelationActionButton>
              </div>
            </TableCell>
          </>
        )}
        footerActions={
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={handleAddNew}
          >
            Add new record
          </Button>
        }
      />
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Remove clinic"
        description="Are you sure you want to remove this clinic?"
        onConfirm={handleDeleteConfirm}
        isPending={isDeleting}
      />
      <ClinicsDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        clinic={editingClinic}
        onSubmit={handleSubmit}
        isPending={isCreating || isUpdating}
      />
    </>
  );
}
